import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExternalLink, Copy, Check, RotateCcw, ChevronRight, AlertTriangle, Lightbulb, BookOpen, Map } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_STYLES = {
  high: { label: "High", className: "badge-ready", color: "hsl(260 42% 38%)" },
  medium: { label: "Medium", className: "badge-emerging", color: "hsl(35 85% 44%)" },
  low: { label: "Low", className: "badge-exploratory", color: "hsl(210 70% 45%)" }
};

const BANDS = ["low", "medium", "high"];

function ReadinessGauge({ band }) {
  const idx = BANDS.indexOf(band);
  return (
    <div data-testid="readiness-gauge" className="mt-2">
      <div className="flex items-stretch gap-1 h-3 rounded-full overflow-hidden bg-muted">
        {BANDS.map((b, i) => (
          <div key={b} className="flex-1 relative transition-colors duration-300"
            style={{ backgroundColor: i <= idx ? BAND_STYLES[b].color : undefined }}>
            {i === idx && <div className="absolute inset-0 animate-pulse" style={{ backgroundColor: BAND_STYLES[b].color, opacity: 0.4 }} />}
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-1">
        {BANDS.map((b, i) => (
          <span key={b} className={`text-[10px] ${i === idx ? 'font-semibold text-foreground' : 'text-muted-foreground/60'}`}
            style={i === idx ? { color: BAND_STYLES[b].color } : undefined}>
            {BAND_STYLES[b].label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function ResultsPage() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [clarityAfter, setClarityAfter] = useState(0);
  const [clarityBefore, setClarityBefore] = useState(3);
  const [copied, setCopied] = useState(false);
  const [saveConsent, setSaveConsent] = useState(false);
  const [gapsOpen, setGapsOpen] = useState(false);

  useEffect(() => {
    const cached = sessionStorage.getItem("synaptick_result");
    const pre = sessionStorage.getItem("synaptick_clarity_before");
    if (cached) { setResult(JSON.parse(cached)); } else { navigate("/"); }
    if (pre) setClarityBefore(parseInt(pre));
  }, [navigate]);

  const handleCopy = useCallback(async () => {
    if (!result?.search_pack) return;
    try {
      await navigator.clipboard.writeText(result.search_pack);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
      toast.success("Filters copied");
    } catch { toast.error("Failed to copy"); }
  }, [result]);

  const handleEducrib = useCallback(() => {
    if (!result?.redirect_url) return;
    const sid = sessionStorage.getItem("synaptick_session_id");
    axios.post(`${API}/analytics/event`, { event_type: "deep_link_clicked", session_id: sid, data: { url: result.redirect_url } }).catch(() => {});
    window.open(result.redirect_url, "_blank");
  }, [result]);

  const handleClarityAfter = useCallback((val) => {
    setClarityAfter(val);
    const sid = sessionStorage.getItem("synaptick_session_id");
    axios.post(`${API}/analytics/event`, {
      event_type: "clarity_after", session_id: sid,
      data: { clarity_before: clarityBefore, clarity_after: val }
    }).catch(() => {});
  }, [clarityBefore]);

  const handleSaveConsent = useCallback(async (checked) => {
    setSaveConsent(checked);
    if (checked && result) {
      toast.success("Anonymised session saved. Thank you.");
    }
  }, [result]);

  if (!result) return null;

  const brief = result.brief;
  const bandInfo = BAND_STYLES[result.readiness_band] || BAND_STYLES.medium;
  const programTypes = brief.suggested_program_types_detailed || brief.suggested_program_types || [];
  const delta = clarityAfter > 0 ? clarityAfter - clarityBefore : null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <span className="font-display text-sm font-semibold text-foreground">Synaptick</span>
          <span className="text-xs text-muted-foreground">Your Clarity Brief</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* ─── Left: Brief ─── */}
          <div className="lg:col-span-2 space-y-6">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
              {/* Direction Summary */}
              <Card data-testid="clarity-brief">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="section-label">Direction Summary</p>
                      <CardTitle className="font-display text-lg sm:text-xl leading-snug">
                        {brief.direction_summary}
                      </CardTitle>
                    </div>
                    <span data-testid="readiness-badge" className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shrink-0 ${bandInfo.className}`}>
                      {bandInfo.label} readiness
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Readiness gauge */}
                  <div className="bg-secondary/40 rounded-lg p-4">
                    <p className="section-label mb-1">Your Readiness</p>
                    <ReadinessGauge band={result.readiness_band} />
                  </div>

                  {/* Priorities */}
                  {brief.priorities && brief.priorities.length > 0 && (
                    <div>
                      <p className="section-label">What matters most</p>
                      <div className="flex flex-wrap gap-2">
                        {brief.priorities.map((p, i) => (
                          <span key={i} className="chip-option selected cursor-default">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Suggested programme types */}
                  <div>
                    <p className="section-label">Suggested programme types</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                      {programTypes.map((pt, i) => (
                        <Card key={i} className="overflow-hidden">
                          <CardContent className="pt-4 pb-3">
                            <p className="text-sm font-semibold text-foreground">{pt.title || pt.type}</p>
                            {pt.why && <p className="text-xs text-muted-foreground mt-1">{pt.why || pt.rationale}</p>}
                            {pt.good_for && (
                              <p className="text-xs text-green-700 mt-2 flex items-start gap-1">
                                <Lightbulb className="w-3 h-3 mt-0.5 shrink-0" /> {pt.good_for}
                              </p>
                            )}
                            {pt.trade_off && (
                              <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {pt.trade_off}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Reality snapshot */}
                  <div>
                    <p className="section-label">Reality snapshot</p>
                    <ul className="space-y-2">
                      {brief.reality_snapshot?.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 mt-1.5 shrink-0" />{b}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Separator />

                  {/* Watch-outs */}
                  {brief.watch_outs?.length > 0 && (
                    <div>
                      <p className="section-label">Watch-outs</p>
                      <ul className="space-y-2">
                        {brief.watch_outs.map((w, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 p-2 rounded-lg">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{w}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Knowledge gaps */}
                  {brief.knowledge_gaps?.length > 0 && (
                    <>
                      <Separator />
                      <Collapsible open={gapsOpen} onOpenChange={setGapsOpen}>
                        <Card className="border-primary/15 bg-accent/20">
                          <CollapsibleTrigger asChild>
                            <button data-testid="knowledge-gaps-toggle" className="w-full text-left">
                              <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <BookOpen className="w-4 h-4 text-primary" />
                                  <span className="text-sm font-semibold text-foreground">What to learn next (before committing)</span>
                                </div>
                                <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${gapsOpen ? 'rotate-90' : ''}`} />
                              </CardHeader>
                            </button>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <CardContent className="pt-0 pb-4">
                              <ul className="space-y-2">
                                {brief.knowledge_gaps.map((g, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />{g}
                                  </li>
                                ))}
                              </ul>
                            </CardContent>
                          </CollapsibleContent>
                        </Card>
                      </Collapsible>
                    </>
                  )}

                  <Separator />

                  {/* Recommended filters table */}
                  <div>
                    <p className="section-label">Recommended search filters</p>
                    <div className="overflow-x-auto mt-2">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Filter</TableHead>
                            <TableHead className="text-xs">Your preference</TableHead>
                            <TableHead className="text-xs">Why it matters</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {brief.recommended_filters?.map((f, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-sm font-medium">{f.filter}</TableCell>
                              <TableCell className="text-sm">{f.value}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{f.why}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  {/* Next actions */}
                  {brief.next_actions?.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <p className="section-label">Next actions</p>
                        <ul className="space-y-2">
                          {brief.next_actions.map((a, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                              <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">{i + 1}</span>
                              {a}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* ─── Right: Actions ─── */}
          <div className="space-y-5 lg:sticky lg:top-20 lg:self-start">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.1 }} className="space-y-5">
              {/* Clarity after */}
              <Card>
                <CardContent className="pt-5">
                  <p className="text-sm font-medium text-foreground mb-3">How clear do you feel now?</p>
                  <div className="flex items-center gap-3">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} data-testid={`clarity-after-${n}`} className={`rating-dot ${clarityAfter === n ? 'active' : ''}`}
                        onClick={() => handleClarityAfter(n)}>{n}</button>
                    ))}
                  </div>
                  {delta !== null && (
                    <p data-testid="clarity-delta" className={`text-xs mt-2 font-medium ${delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                      {delta > 0 ? `+${delta}` : delta} clarity shift{delta > 0 ? ' (improved)' : delta < 0 ? ' (decreased)' : ' (no change)'}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* CTA */}
              <Card>
                <CardContent className="pt-5 space-y-3">
                  <Button data-testid="continue-educrib-button" onClick={handleEducrib} className="w-full rounded-[12px] gap-2">
                    Continue to Educrib <ExternalLink className="w-4 h-4" />
                  </Button>
                  <a href="https://synaptick.ai" target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors py-1">
                    <Map className="w-3 h-3" /> Get a full learning roadmap at Synaptick.ai <ExternalLink className="w-3 h-3" />
                  </a>
                  <Separator />
                  <p className="text-xs text-muted-foreground">If Educrib does not auto-fill, copy these filters:</p>
                  <div data-testid="search-pack" className="bg-secondary/60 rounded-lg p-3 text-xs text-foreground whitespace-pre-line min-h-[50px]">
                    {result.search_pack}
                  </div>
                  <Button data-testid="copy-filters-button" variant="secondary" size="sm" onClick={handleCopy} className="w-full rounded-[12px] gap-2">
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? "Copied" : "Copy filters"}
                  </Button>
                </CardContent>
              </Card>

              {/* Consent */}
              <Card>
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">Save anonymised session</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Helps us improve this tool.</p>
                    </div>
                    <Switch data-testid="consent-toggle" checked={saveConsent} onCheckedChange={handleSaveConsent} />
                  </div>
                </CardContent>
              </Card>

              <Button data-testid="start-over-button" variant="secondary" onClick={() => {
                sessionStorage.removeItem("synaptick_result");
                sessionStorage.removeItem("synaptick_clarity_before");
                sessionStorage.removeItem("synaptick_sprint_state");
                navigate("/");
              }} className="w-full rounded-[12px] gap-2"><RotateCcw className="w-4 h-4" /> Start over</Button>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-4">
        <p className="text-xs text-center text-muted-foreground">
          This is guidance to help you decide what to explore \u2014 not official admissions or career advice.
        </p>
      </div>

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-6 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Synaptick Clarity Layer</span>
          <a href="/readme" className="hover:text-foreground transition-colors">About</a>
        </div>
      </footer>
    </div>
  );
}
